window.ttx = window.ttx || {};
ttx.command = {};

// 判断是否为模态框/侧滑抽屉模式：caller 传入或设计器配置均可
function _getModalDelay(viewmeta, params, extraConfig) {
    var isModal = (params && params.templateType === 'modal')
        || (viewmeta?.view || viewmeta?.template?.view)?.templateType === 'modal';
    return {
        delay: isModal || undefined,
        extraConfig: isModal ? (extraConfig || {}) : undefined
    };
}

// 从 VM 和 viewmeta 获取 delay + extraConfig（用于 SDK 命令回调中构造 addItem 参数）
function _getModalOpts(newVm, viewmeta) {
    var md = _getModalDelay(viewmeta, null, null);
    var extra = newVm?.getCache?.('params')?.extraConfig || {};
    return { delay: md.delay, extraConfig: Object.assign({}, md.extraConfig, extra) };
}

/**
 * 解析命令参数中的占位符：${row.xxx} 取当前行字段，${vm.xxx} 取当前页面数据字段
 * 支持字符串、数组、对象嵌套；非字符串原样返回
 */
function resolveCommandArgs(value, row, vm) {
    if (typeof value === 'string') {
        return value.replace(/\$\{(row|vm)\.([\w.$]+)\}/g, function (match, scope, path) {
            var source = scope === 'row'
                ? (row || {})
                : (vm?.getData?.() || vm?._get_data?.('value') || {});
            var result = path.split('.').reduce(function (acc, key) {
                return (acc === undefined || acc === null) ? undefined : acc[key];
            }, source);
            return (result === undefined || result === null) ? '' : String(result);
        });
    }
    if (Array.isArray(value)) {
        return value.map(function (item) { return resolveCommandArgs(item, row, vm); });
    }
    if (value && typeof value === 'object') {
        var next = {};
        Object.keys(value).forEach(function (key) {
            next[key] = resolveCommandArgs(value[key], row, vm);
        });
        return next;
    }
    return value;
}

/** 解析按钮/操作列上配置的命令参数（JSON 字符串或对象）；非法 JSON 返回 null 并提示 */
function _parseCommandArgs(ctx) {
    var raw = ctx?.action?.commandArgs || ctx?.button?.commandArgs || '';
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try {
        return JSON.parse(raw);
    } catch (e) {
        ttx.toast?.error?.('命令参数不是合法 JSON');
        return null;
    }
}

/**
 * 刷新页面数据：列表页（含 modal 列表页）按当前查询条件重新取数；
 * 卡片页（含模态卡片/抽屉卡片）按单据 id 重拉详情；其余场景派发 refresh 事件兜底。
 *
 * 卡片页必须在这里自己取数：模态/抽屉卡片由 Meta.renderModalPanes 直接渲染 Container，
 * 没有挂 Meta 组件，也就不会注册 'refresh' 事件监听，只派发 execute('refresh') 会静默无事发生。
 */
function _refreshVmData(vm) {
    if (!vm) return;
    var layout = vm.getCache?.('layoutType');
    var params = vm.getParams?.() || {};
    var billno = vm._get_data?.('serviceCode') || params.billno || (params.params && params.params.billno) || '';
    if (layout === 'list' && billno && ttx.bill?.list) {
        var pg = vm.getCache?.('pagination') || {};
        ttx.bill.list(null, { billno: billno }, { params: params, pageNum: pg.current || 1, pageSize: pg.pageSize || 20 }, function (res) {
            var d = res?.data || {};
            var list = d.list || d.data || [];
            vm._set_data?.('value', Array.isArray(list) ? list : [], true);
            try { vm.setCache?.('pagination', Object.assign({}, pg, { total: d.total || list.length })); } catch (e) {}
            vm.execute?.('dataRefresh');
        });
        return;
    }
    if (layout === 'card' && billno && ttx.bill?.detail) {
        // 页签卡片由 Meta 注册的 refresh 监听负责取数（含扩展脚本自定义逻辑），这里不重复请求
        if (vm.hasEvent?.('refresh')) {
            vm.execute?.('refresh');
            return;
        }
        var value = vm._get_data?.('value');
        var vmData = typeof vm.getData === 'function' ? (vm.getData() || {}) : {};
        // id 兜底顺序：已加载单据数据 > 打开参数 query/params > 表单数据
        var id = (value && !Array.isArray(value) ? value.id : '')
            || (params.query && params.query.id)
            || (params.params && params.params.id)
            || params.id
            || vmData.id;
        // 新增态没有 id：保持当前录入内容不动
        if (id) {
            var load = ttx.loading?.showInCurrentTab?.();
            ttx.bill.detail(null, { billno: billno }, { id: id }, function (res) {
                load?.hide?.();
                if (res && res.data) {
                    vm._set_data?.('value', res.data, true);
                    // 重新广播模式：详情加载后按钮显隐（visibleModes/visibleRule）需要重算
                    vm.execute?.('modeChange', vm._get_data?.('currentMode') || 'browse');
                    vm._scheduleFormulas?.('*');
                    vm.execute?.('dataRefresh', { source: 'card-refresh' });
                }
            });
            return;
        }
    }
    vm.execute?.('refresh');
}

/** 命令执行成功后的结果动作：refresh / close / toast / none */
function _applyCommandResult(args, vm, result, res) {
    // 脚本返回的 message 更精确（如「已提交修复 2 条」），优先展示；否则用按钮配置的固定文案
    var successMsg = (result && typeof result === 'object' && result.message) || args?.successMessage;
    if (successMsg) ttx.toast?.success?.(successMsg);
    var action = args?.resultAction || 'none';
    if (action === 'refresh') {
        _refreshVmData(vm);
    } else if (action === 'close') {
        vm?.execute?.('requestClose', { refreshData: true, source: 'run-script' });
    } else if (action === 'toast' && !successMsg) {
        ttx.toast?.success?.('执行成功');
    }
    vm?.execute?.('afterCommandResult', { result: result, res: res });
}

/** 命令执行失败处理：toast / silent / abort（abort 仅阻止后续流程，当前按钮无后续流程） */
function _handleCommandFail(args, err) {
    var mode = args?.failMode || 'toast';
    if (mode === 'silent') return;
    ttx.toast?.error?.(args?.failMessage || err?.message || '执行失败');
}

const COMMAND_ALIAS_MAP = {
    '提交': 'submit',
    '审批': 'approve',
    '审核': 'approve',
    '通过': 'approve',
    '审批流': 'workflow',
    '驳回': 'reject',
    '修改': 'modify',
    '撤回': 'withdraw',
};

function normalizeCommandAlias(command) {
    if (typeof command !== 'string') return command;
    const trimmed = command.trim();
    const lower = trimmed.toLowerCase();
    return COMMAND_ALIAS_MAP[trimmed] || COMMAND_ALIAS_MAP[lower] || lower;
}

// ============================================================
// 命令执行引擎（大小写不敏感）
// ============================================================
ttx.command.execute = function (command, data) {
    if (!command) return;
    const key = normalizeCommandAlias(command);
    var vmId = data?.vm ? (data.vm._get_data?.('serviceCode') || data.vm.getParams?.()?.billno || 'unknown') : 'null';
    console.log('[addRow][CMD入口] execute: ' + command + ' → ' + key + ' containerKey=' + (data?.containerKey || 'undefined') + ' vm=' + vmId);
    ttx.console.log('[CMD] execute:', command, '→', key, 'vm:', !!data?.vm);
    if (this.commands[key]) {
        return this.commands[key](data);
    }
    console.warn('[addRow][CMD入口] 命令未找到: ' + key);
};

ttx.command.commands = {};
ttx.command.scopes = {};

ttx.command.register = function (command, callback) {
    this.commands[normalizeCommandAlias(command)] = callback;
};

ttx.command.unregister = function (command) {
    const key = normalizeCommandAlias(command);
    if (this.commands[key]) {
        delete this.commands[key];
    }
};

/** 按场景批量注册命令（card / list / table），便于分组管理和避免命名冲突 */
ttx.command.registerScope = function (scope, commands) {
    if (!scope || !commands) return;
    for (const [name, handler] of Object.entries(commands)) {
        this.register(name, handler);
    }
    this.scopes[scope] = Object.keys(commands);
};

ttx.command.executeCommand = function (command, data) {
    ttx.command.execute(command, data);
};

// ============================================================
// VM 工具方法
// ============================================================

/** 从 VM 获取 billno（优先级：params.billno > params.billNo > serviceCode > template.serviceCode） */
function getBillno(vm) {
    if (!vm) return '';
    const params = vm.getParams?.() || {};
    return params.billno || params.billNo || vm._get_data?.('serviceCode') || vm._get_data?.('template')?.serviceCode || '';
}

/**
 * 计算系统级页签的 tabKey，逻辑必须和 core 工程 Transfer.openService 对齐：
 *   key = menuurl || billno
 *   mode='add' 时追加 ':add' 后缀（避免和列表页 key 冲突）
 * 这个 key 是 Transfer.closeService 的入参。
 *
 * 注意：Transfer.openService 接收的 params 是外层结构（含 params.params.mode），
 * 而 VM.getParams() 返回的是内层（即原来的 data.params），所以从 VM 读 mode 时
 * 是 vm.getParams().mode，不是 vm.getParams().params.mode。
 */
function getSystemTabKey(vm) {
    if (!vm) return '';
    // 优先读 openServiceWithVM 创建标签时存储的精确 tab key，
    // 避免从 params 推导时的嵌套层级歧义（vm.getParams() 可能返回内层/外层不一致）
    const cached = vm.getCache?.('__tabKey');
    if (cached) return cached;
    // fallback：从 params 推导（兼容未走 openServiceWithVM 的历史路径）
    const params = vm.getParams?.() || {};
    const key = params.menuurl || params.billno || getBillno(vm);
    if (!key) return '';
    const mode = params?.params?.mode || params.mode;
    return mode === 'add' ? (key + ':add') : key;
}

/**
 * 系统级多标签场景下的关闭：当 VM 挂了 newOpenTabs 缓存时，
 * back/return/cancel 命令应该关闭整个系统级页签（通过 Transfer.closeService），
 * 而不是走 requestClose（那是页签栈内回退到上一级，在多标签下没有"父页面"概念）。
 * @returns {boolean} true 表示已处理（调用了 closeService），false 表示未处理（调用方走原逻辑）
 */
function closeSystemTabIfNeed(vm) {
    if (!vm) return false;
    // newOpenTabs 缓存统一由 core 侧 Pane render 设置在系统级标签的根 VM（panes[0]）上，
    // 内部栈内的子 VM 不会有此缓存，back 时走 requestClose 正常弹栈
    if (!vm.getCache?.('newOpenTabs')) return false;
    const transferRef = window.__transferRef;
    if (!transferRef || typeof transferRef.closeService !== 'function') return false;
    const tabKey = getSystemTabKey(vm);
    if (!tabKey) return false;
    vm.execute?.('beforeBack');
    transferRef.closeService(tabKey);
    return true;
}

/** 切换 VM 的编辑/浏览模式，同时持久化状态 + 通知所有监听器 */
function switchMode(vm, mode) {
    if (!vm) return;
    vm._set_data?.('currentMode', mode, true);
    vm._set_data?.('pageMode', mode, true);
    vm.execute('modeChange', mode);
}

/** 备份当前表单数据（编辑前调用，取消时用于还原） */
function backupData(vm) {
    const currentData = vm?.getData?.();
    if (currentData && typeof currentData === 'object') {
        vm.setCache?.('_editBackup', JSON.parse(JSON.stringify(currentData)));
    }
}

/** 还原编辑前的备份数据 */
function restoreData(vm) {
    const backup = vm?.getCache?.('_editBackup');
    if (backup) {
        vm._set_data?.('value', backup, true);
        vm.setCache?.('_editBackup', null);
    }
}

/**
 * 从命名空间对象中解出列表数组。
 *
 * 列表加编码分类后 value 由直接数组变为 { "tableCode_xxx": [...] } 格式，
 * 此处按命名空间格式拆包，若已是数组则原样返回。
 * @param {any} val - 可能是数组、命名空间对象或其他值
 * @returns {Array|any} 拆包后的数组，或原值
 */
function _unwrapNamespacedArray(val) {
    if (Array.isArray(val)) return val;
    if (val && typeof val === 'object') {
        for (var k in val) {
            if (val.hasOwnProperty(k) && Array.isArray(val[k])) {
                return val[k];
            }
        }
    }
    return val;
}

/**
 * 判断当前卡片页是否开启"实时保存草稿"（由设计器 rootConfig.draftAutoSave 下发的页面配置控制）
 * @param {object} vm
 * @returns {boolean}
 */
function draftAutoSaveEnabled(vm) {
    return !!vm?._get_data?.('template')?.view?.draftAutoSave;
}

/**
 * 切换单据：从父级列表 VM 的 dataSource 找到当前记录，偏移 direction 位后加载新详情
 * @param {object} ctx - { vm, ... }
 * @param {number} direction -1 上一条，1 下一条
 */
function reloadBillDetail(vm, source) {
    if (!vm) return;
    const vmParams = vm.getParams?.() || {};
    const vmData = vm.getData?.() || {};
    const billno = getBillno(vm);
    const id = vmData.id || vmParams?.params?.id || vmParams?.id;
    if (!billno || !id) {
        vm.execute?.('refresh');
        return;
    }

    const load = ttx.loading?.showInCurrentTab?.();
    ttx.bill.detail(null, { billno }, { id }, function (res) {
        load?.hide?.();
        const detailData = res?.data || res;
        if (detailData && typeof detailData === 'object') {
            vm._set_data?.('value', detailData, true);
            const currentMode = vm._get_data?.('currentMode') || 'browse';
            vm.execute?.('modeChange', currentMode);
            vm.execute?.('dataRefresh', { source: source || 'workflow-detail-refresh' });
        }
        vm.execute?.('refresh');
    });
}

function switchBill(ctx, direction) {
    var vm = ctx.vm;
    if (!vm) { ttx.log.warn('[switchBill] ctx.vm 为空'); return; }

    var dirName = direction === -1 ? '上一张' : '下一张';
    ttx.log.info('[switchBill] 开始执行 ' + dirName);

    // 找到父级列表 VM（打开卡片页时通过 setCache('parentViewModel') 关联）
    var listVm = vm.getCache?.('parentViewModel');
    // 回退策略：若 parentViewModel 未缓存，从调试注册表查找有数据的 VM
    if (!listVm) {
        var vms = ttx.debug?.vms || {};
        for (var key in vms) {
            if (vms.hasOwnProperty(key)) {
                var candidate = vms[key];
                if (candidate && candidate !== vm) {
                    // 兼容编码分类：dataSource / value 可能是命名空间对象
                    var ds = _unwrapNamespacedArray(candidate._get_data?.('dataSource'));
                    var val = _unwrapNamespacedArray(candidate.getData?.());
                    if ((Array.isArray(ds) && ds.length) || (Array.isArray(val) && val.length)) {
                        listVm = candidate;
                        ttx.log.info('[switchBill] 通过 ttx.debug.vms 回退找到列表 VM: ' + key);
                        break;
                    }
                }
            }
        }
    }
    if (!listVm) {
        ttx.log.warn('[switchBill] 未找到 parentViewModel，且无可用列表 VM');
        ttx.toast?.warning?.('无列表数据，无法切换');
        return;
    }

    // 获取列表数据：优先 dataSource，回退到 value（searchModel 将查询结果存在 value 里）
    // 兼容编码分类：value 可能是 { "tableCode": [...] } 而非直接数组
    var dataSource = listVm._get_data?.('dataSource');
    dataSource = _unwrapNamespacedArray(dataSource);
    if (!Array.isArray(dataSource) || !dataSource.length) {
        var val = _unwrapNamespacedArray(listVm.getData?.());
        if (Array.isArray(val) && val.length) {
            dataSource = val;
            ttx.log.info('[switchBill] 通过 getData()（value）获取列表数据, 长度=' + dataSource.length);
        }
    }
    if (!Array.isArray(dataSource) || !dataSource.length) {
        ttx.log.warn('[switchBill] dataSource 和 value 均为空');
        ttx.toast?.warning?.('列表无数据');
        return;
    }
    ttx.log.info('[switchBill] 列表数据长度=' + dataSource.length);

    var currentId = vm.getParams?.()?.id
        || vm.getParams?.()?.params?.id
        || vm._get_data?.('value')?.id;
    ttx.log.info('[switchBill] currentId=' + currentId);
    if (!currentId) { ttx.log.warn('[switchBill] 无法获取当前记录 id'); return; }

    // 找到当前记录在列表中的位置
    var currentIndex = -1;
    for (var i = 0; i < dataSource.length; i++) {
        if (dataSource[i].id === currentId) {
            currentIndex = i;
            break;
        }
    }
    if (currentIndex === -1) {
        ttx.log.warn('[switchBill] 在 dataSource 中未找到 currentId=' + currentId);
        return;
    }
    ttx.log.info('[switchBill] currentIndex=' + currentIndex);

    var targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= dataSource.length) {
        ttx.log.info('[switchBill] ' + dirName + ' 越界，targetIndex=' + targetIndex + ', dataSource.length=' + dataSource.length);
        ttx.toast?.warning?.(direction === -1 ? '已是第一张' : '已是最后一张');
        return;
    }

    var targetRecord = dataSource[targetIndex];
    var targetId = targetRecord.id || targetRecord.ID || targetRecord._id || targetRecord.key;
    if (!targetId) { ttx.log.warn('[switchBill] targetRecord 无 id'); return; }
    ttx.log.info('[switchBill] 目标记录 id=' + targetId + ', 加载详情数据');

    // 直接调详情接口，更新当前 VM 数据（不重新初始化页面）
    var billno = getBillno(vm);
    var load = ttx.loading.showInCurrentTab();
    ttx.bill.detail(null, { billno: billno }, { id: targetId }, function (res) {
        load.hide();
        if (res && res.data) {
            // 将详情数据写入当前 VM
            vm._set_data?.('value', res.data, true);
            // 同步更新 VM 自身的 params.id（确保后续切换基于新 id 定位）
            var curParams = vm.getParams?.() || {};
            if (curParams.params) {
                curParams.params.id = targetId;
            } else {
                curParams.id = targetId;
            }
            // 触发 modeChange 事件刷新 UI：
            //   Meta 组件 → setState({ currentMode }) → 整树重渲染
            //   Form 组件 → _handleModeChange → form.setFieldsValue(data)（编辑态）
            //   browse 态 → getFieldText() 直接从 VM 取值，重渲染即生效
            var currentMode = vm._get_data?.('currentMode') || 'browse';
            vm.execute?.('modeChange', currentMode);
            ttx.log.info('[switchBill] ' + dirName + ' 加载完成, id=' + targetId);
        }
    });
}

// ============================================================
// 卡片页命令（card scope）
// ============================================================
ttx.command.registerScope('card', {

    save(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        if (vm.execute('beforeSave') === false) return;
        const data = vm.getData?.() || {};
        const billno = getBillno(vm);
        const params = ctx.params || vm.getParams?.() || {};
        ttx.bill.save(null, { billno }, { data, params: { ...params, billno } }, function (res) {
            // 将保存返回的数据写回 VM（add 后 id 回填，确保下次保存走更新）
            const savedData = res?.data || res;
            if (savedData) {
                vm._set_data?.('value', savedData, true);
            }
            // 新增保存成功后，确认占用编码（递增 currentSeq）
            ttx.codeRule?.consume(billno, function () {});
            vm.execute('afterSave', res);
            ttx.draft?.clearAll(vm);
            ttx.toast.success('保存成功');
            const stayAfterSave = vm._get_data?.('template')?.view?.stayAfterSave;
            if (stayAfterSave) {
                switchMode(vm, 'browse');
            } else {
                vm.execute('requestClose', { refreshData: true, source: 'card-save-close' });
            }
        });
    },

    edit(ctx) {
        const { vm } = ctx;
        if (!vm) return;

        // 状态校验：开立态（草稿 0 / 被驳回 3）可直接编辑；
        // 其它状态仅当当前审批环节开放了字段编辑权限时允许进入编辑态。
        // 注意：只有启用审批的单据才有 approval_state，无审批单据不能拿 status/billstatus 兜底，
        // 否则业务状态（如启用/停用）会被误判成审批状态而拦截编辑。
        const value = vm._get_data?.('value') || vm.getData?.() || {};
        const approvalState = value.approval_state;
        const hasApprovalState = approvalState !== undefined && approvalState !== null && approvalState !== '';
        const status = hasApprovalState ? String(approvalState) : '';
        const editableStatus = !hasApprovalState || status === '0' || status === '3';
        if (!editableStatus) {
            const perms = vm.__approvalFieldPermissions || null;
            const hasEditable = !!perms && Object.keys(perms).some(
                (key) => perms[key] && perms[key].editable === true,
            );
            if (!hasEditable) {
                ttx.toast?.warning?.('当前单据状态不允许编辑');
                return;
            }
        }

        backupData(vm);
        switchMode(vm, 'edit');
        // 编辑态按页面配置开启草稿自动保存
        if (draftAutoSaveEnabled(vm)) ttx.draft?.enableAutoSave(vm);
    },

    modify(ctx) {
        const { vm } = ctx;
        if (!vm) return;

        // TODO: 状态校验暂时注释，后续改为根据系统参数设置
        // const value = vm.getData?.() || vm._get_data?.('value') || {};
        // const status = String(value.billstatus ?? value.state ?? value.status ?? '');
        // if (status !== '1' && status !== '4') {
        //     ttx.toast?.warning?.('仅审核中或驳回到制单状态可修改');
        //     return;
        // }

        backupData(vm);
        switchMode(vm, 'edit');
        ttx.draft?.enableAutoSave(vm);
    },

    browse(ctx) {
        // 浏览态关闭草稿自动保存
        ttx.draft?.disableAutoSave(ctx.vm);
        switchMode(ctx.vm, 'browse');
    },

    cancel(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        // 用 pageMode 而非 currentMode 判断是否为新增页面：
        // currentMode 在新增和编辑时都是 'edit'，无法区分；pageMode 在 voucher.js 初始化时写入原始 mode（add/edit/browse），不会混淆
        const isAdd = vm._get_data?.('pageMode') === 'add';
        if (isAdd) {
            ttx.modal.confirm({
                title: '提示',
                content: '确定要取消新增吗？取消后当前页面将关闭。',
                onConfirm: function () {
                    // 系统级多标签：直接关闭系统级页签，不走 requestClose（页签栈内回退）
                    if (closeSystemTabIfNeed(vm)) return;
                    // 主动取消新增：清除本次自动保存的草稿，避免下次新增误弹「草稿恢复」
                    ttx.draft?.clearAuto(vm);
                    vm.execute('requestClose', { refreshData: true, source: 'card-cancel-add' });
                },
            });
            return;
        }
        // 非新增的取消：系统级多标签下也应直接关闭页签（与 back 同语义）
        if (closeSystemTabIfNeed(vm)) return;
        restoreData(vm);
        switchMode(vm, 'browse');
    },

    add(ctx) {
        const { vm } = ctx;
        const billno = getBillno(vm);
        const metaid = vm?._get_data?.('template')?.view?.cCardKey || vm?.getParams?.()?.query?.metaid || '';
        // openParams：完整的打开参数对象，由 byBillNo 注入 newOpenTabs 字段
        // 透传给 addItem，让 Pane 的 addItem 拦截器能读到 params.newOpenTabs 决定是否新开系统级标签
        var openParams = {
            billtype: 'card',
            billno,
            params: { mode: 'add', query: { metaid } }
        };
        ttx.loader.runCommandLine('bill', openParams, vm, function (newVm, viewmeta, title) {
            // 新增时按页面配置检测缓存草稿，有则弹窗恢复
            if (draftAutoSaveEnabled(newVm)) ttx.draft?.checkOnAdd(newVm);
            // 新增态按页面配置开启自动保存
            if (draftAutoSaveEnabled(newVm)) ttx.draft?.enableAutoSave(newVm);
            var _mo1 = _getModalOpts(newVm, viewmeta);
            ttx.portalactions.addItem(billno, {
                title,
                content: { vm: newVm, metaData: viewmeta, delay: _mo1.delay, extraConfig: _mo1.extraConfig },
                parent: vm,
                // 透传打开参数（含 newOpenTabs），供下游拦截器读取
                params: openParams,
            });
            // addItem 已同步 dispatch，但 Form 要等 React commit 后才挂载。
            // 延迟触发 modeChange，确保组件已注册监听。
            // 注意：不改 pageMode！pageMode 是"页面原始 mode"的语义（add/edit/browse），不能被覆盖。
            // 这里改成 edit 会导致 cancel 命令的 isAdd 判断失效（pageMode 不再是 'add'，不弹 confirm）。
            // currentMode 改成 edit 是对的（新增态允许编辑），但 pageMode 必须保持 add。
            setTimeout(function () {
                newVm._set_data?.('currentMode', 'edit', true);
                newVm.execute?.('modeChange', 'edit');
            }, 50);
        });
    },

    /**
     * 全选 / 取消全选（表格工具栏按钮）
     * 已有选中行数 = 数据总数时执行取消全选，否则全选当前数据。
     * 支持 commandArgs: { mode: 'all' | 'none' | 'toggle' }（默认 toggle）
     */
    selectall(ctx = {}) {
        const { vm, containerKey } = ctx;
        var args = _parseCommandArgs(ctx) || {};
        var tableApi = ctx.tableApi
            || (vm && containerKey ? vm.getCache?.('tableApi_' + containerKey) : null)
            || vm?.getCache?.('tableApi');
        if (!tableApi) {
            ttx.toast?.warning?.('未找到表格，无法全选');
            return;
        }
        var rows = tableApi.getData?.() || [];
        if (!rows.length) {
            ttx.toast?.warning?.('当前没有可选择的记录');
            return;
        }
        var selected = tableApi.getSelectRows?.() || tableApi.getSelectedRows?.() || [];
        var mode = args.mode || 'toggle';
        var selectAll = mode === 'all' ? true : (mode === 'none' ? false : selected.length !== rows.length);
        if (selectAll) {
            if (typeof tableApi.selectAll === 'function') {
                tableApi.selectAll();
            } else {
                tableApi.setSelectedIndices?.(rows.map(function (_, i) { return i; }));
            }
            ttx.toast?.success?.('已全选当前 ' + rows.length + ' 条记录');
        } else {
            if (typeof tableApi.clearSelection === 'function') {
                tableApi.clearSelection();
            }
            ttx.toast?.info?.('已取消全选');
        }
    },

    /**
     * 打开页面（系统跳转命令）：按命令参数打开指定页面，页面自身配置为 modal 时自动以模态框打开。
     * 命令参数（commandArgs，JSON）：
     *   {
     *     "billno": "cr_record",              // 目标页面所属业务对象（必填，缺省用当前页面）
     *     "metaid": "xxx",                    // 目标页面元数据 id
     *     "mode": "browse",                   // browse / edit / add
     *     "id": "",                           // 编辑/浏览时的记录 id
     *     "templateType": "modal",            // 可选，强制以模态框打开
     *     "title": "信用修复",                 // 可选，覆盖弹框标题
     *     "query": { "customer_id": "${row.id}" },
     *     "carryParams": { "xxx": "${vm.xxx}" }
     *   }
     * 占位符：${row.xxx} 取当前行字段，${vm.xxx} 取当前页面数据字段
     */
    openpage(ctx = {}) {
        const { vm, row } = ctx;
        var rawArgs = ctx.action?.commandArgs || ctx.button?.commandArgs || '';
        var args = {};
        if (rawArgs) {
            try {
                args = typeof rawArgs === 'string' ? JSON.parse(rawArgs) : rawArgs;
            } catch (e) {
                ttx.toast?.error?.('打开页面命令参数不是合法 JSON');
                return;
            }
        }
        var billno = args.billno || getBillno(vm);
        if (!billno) {
            ttx.toast?.warning?.('打开页面缺少 billno');
            return;
        }
        var query = resolveCommandArgs(args.query || {}, row, vm);
        var carryParams = resolveCommandArgs(args.carryParams || {}, row, vm);
        // carryData：新增态表单默认值，优先级高于 query。
        // 业务场景：客户数据池行点「加微信」「写跟进」时，把当前行的 pool_id（及企业名称、手机号等）
        // 带入卡片新增表单，保存后卡片记录凭 pool_id 关联回数据池行。
        var carryData = resolveCommandArgs(args.carryData || {}, row, vm);
        var metaid = args.metaid || query.metaid || '';
        if (metaid && !query.metaid) query.metaid = metaid;
        var openParams = {
            billtype: args.billtype || 'card',
            billno: billno,
            params: {
                mode: args.mode || 'browse',
                id: resolveCommandArgs(args.id, row, vm),
                templateType: args.templateType,
                // 传入弹框标题，byBillNo 的 modal 分支优先使用
                title: args.title,
                query: query,
                carryParams: Object.keys(carryParams).length ? carryParams : undefined,
                carryData: Object.keys(carryData).length ? carryData : undefined,
            },
            extraConfig: args.extraConfig,
        };
        // 过滤 undefined，避免覆盖默认逻辑
        Object.keys(openParams.params).forEach(function (k) {
            if (openParams.params[k] === undefined) delete openParams.params[k];
        });
        // 指定 modal：直接走 byBillNo 内置的模态框分支（不传回调 → 自动 addItem + delay + extraConfig）
        if (openParams.params.templateType === 'modal') {
            ttx.loader.runCommandLine('bill', openParams, vm);
            return;
        }
        ttx.loader.runCommandLine('bill', openParams, vm, function (newVm, viewmeta, title) {
            var view = (viewmeta?.view || viewmeta?.template?.view) || {};
            // 目标页面自身 templateType=modal 时同样按模态框打开（延迟标记）
            var modalOpts = _getModalDelay(viewmeta, openParams.params, args.extraConfig);
            ttx.portalactions.addItem(billno, {
                title: args.title || view.pageName || title || '',
                content: { vm: newVm, metaData: viewmeta, delay: modalOpts.delay, extraConfig: modalOpts.extraConfig },
                parent: vm,
                params: openParams,
            });
        });
    },

    /**
     * 执行后端脚本：命令参数 { scriptId | scriptCode, event, resultAction, successMessage, failMode }
     * 脚本在服务端沙箱执行（exports.main），执行日志落 sys_script_log
     */
    runbackscript(ctx = {}) {
        const { vm, row, selectedRows } = ctx;
        var args = _parseCommandArgs(ctx);
        if (args === null) return;
        if (!args.scriptId && !args.scriptCode) {
            ttx.toast?.warning?.('执行后端脚本缺少 scriptId 或 scriptCode');
            return;
        }
        var vmData = vm?.getData?.() || {};
        var rows = Array.isArray(selectedRows) ? selectedRows : [];
        var event = Object.assign({
            billno: getBillno(vm),
            billId: vmData.id || '',
            data: vmData,
            // 表格工具栏按钮：当前勾选行
            selectedRows: rows,
            selectedIds: rows.map(function (r) { return r && r.id; }).filter(Boolean),
        }, resolveCommandArgs(args.event || {}, row, vm));
        var load = ttx.loading?.showInCurrentTab?.();
        ttx.ajax.xhrUtil({
            url: '/api/script/execute',
            method: 'post',
            data: { scriptId: args.scriptId, scriptCode: args.scriptCode, event: event },
            showErrorToast: false,
            success: function (res) {
                load?.hide?.();
                _applyCommandResult(args, vm, res?.data?.result, res);
            },
            error: function (err) {
                load?.hide?.();
                _handleCommandFail(args, err);
            },
        });
    },

    /**
     * 执行前端脚本：命令参数 { scriptId | scriptCode, event, resultAction, successMessage, failMode }
     * 脚本在前端执行（exports.main(event, context)），可用于改表单值、刷新、打开页面等
     */
    runfrontscript(ctx = {}) {
        const { vm, row } = ctx;
        var args = _parseCommandArgs(ctx);
        if (args === null) return;
        if (!args.scriptId && !args.scriptCode) {
            ttx.toast?.warning?.('执行前端脚本缺少 scriptId 或 scriptCode');
            return;
        }
        var event = Object.assign({
            vm: vm,
            row: row || null,
            button: ctx.button || null,
            action: ctx.action || null,
            billno: getBillno(vm),
            values: vm?.getData?.() || {},
            params: vm?.getParams?.() || {},
        }, resolveCommandArgs(args.event || {}, row, vm));
        var load = ttx.loading?.showInCurrentTab?.();
        Promise.resolve(ttx.script?.run?.({
            scriptId: args.scriptId,
            scriptCode: args.scriptCode,
            event: event,
            vm: vm,
        }))
            .then(function (result) {
                load?.hide?.();
                if (result === false) {
                    _handleCommandFail(args, { message: args.failMessage || '脚本返回 false，已终止' });
                    return;
                }
                _applyCommandResult(args, vm, result);
            })
            .catch(function (err) {
                load?.hide?.();
                _handleCommandFail(args, err || {});
            });
    },

    delete(ctx) {
        const { vm, callback } = ctx;
        if (!vm) return;
        if (vm.execute('beforeDelete') === false) return;
        const data = vm.getData?.() || {};
        const billno = getBillno(vm);
        const params = vm.getParams?.() || {};
        // id 优先从 VM data 取（表单数据），其次从页面 params 取（编辑模式下的路由参数）
        const id = data.id || params?.params?.id || params?.id || '';
        // 返回 Promise：确认→show loading→调接口→hide loading→关闭页签
        return new Promise((resolve) => {
            ttx.modal.confirm({
                title: '确定要删除吗？',
                content: '删除后将无法恢复',
                onConfirm: function () {
                    const load = ttx.loading.showInCurrentTab();
                    const query = ttx.utils.transferQuery({ billno, id });
                    ttx.ajax.xhrUtil({
                        url: '/api/bill/delete' + query,
                        method: 'post',
                        data: { data, billno },
                        success: function (res) {
                            load.hide();
                            vm.execute('afterDelete', res);
                            callback?.();
                            resolve();
                            vm.execute('requestClose', { refreshData: true, source: 'card-delete' });
                        },
                        fail: function (err) {
                            load.hide();
                            // 兜底：清掉可能残留的实例型 loading，避免接口报错后转圈不消失
                            ttx.loading?.hideAll?.();
                            resolve();
                        }
                    });
                },
                onCancel: function () {
                    resolve();
                }
            });
        });
    },

    async submit(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        // 提交只是把草稿单据提交审核，不打开审批流面板（审批流环节用「审批流」按钮单独查看）
        if (vm.execute('beforeSubmit', ctx) === false) return;
        const vmData = vm.getData?.() || {};
        const billno = getBillno(vm);
        const data = { ...vmData };
        ttx.bill.submit(null, { billno }, { data, billno }, function (res) {
            const nextData = res?.data || res;
            if (nextData && typeof nextData === 'object') {
                vm._set_data?.('value', nextData, true);
                const currentMode = vm._get_data?.('currentMode') || 'browse';
                vm.execute?.('modeChange', currentMode);
                vm.execute?.('dataRefresh', { source: 'workflow-submit' });
            }
            reloadBillDetail(vm, 'workflow-submit-detail');
            ttx.approvalTask?.refreshCount?.();
            // 已打开的审批侧边栏同步刷新到最新环节
            window.dispatchEvent(new CustomEvent('ttx:workflow:refresh', {
                detail: { billno, id: vmData.id || nextData?.id },
            }));
            vm.execute('afterSubmit', res);
        });
    },

    async approve(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        const result = await ttx.workflow?.open?.('approve', vm, {
            actionLabel: ctx?.button?.name || '审批',
            // 面板内连续操作（下一环节审批人仍是自己）时复用已有节点，避免重开时闪回默认三步
            steps: ctx?.approvalSteps,
        });
        if (!result?.confirmed) return;
        const remark = result?.remark || '';
        // 审批侧边栏里点「驳回」：直接走驳回链路，不再二次弹面板
        if (result?.action === 'reject') {
            return ttx.command.execute('reject', { ...ctx, skipPanel: true, remark });
        }
        if (vm.execute('beforeApprove', { ...ctx, remark }) === false) return;
        const vmParams = vm.getParams?.() || {};
        const vmData = vm.getData?.() || {};
        const billno = getBillno(vm);
        const id = vmData.id || vmParams?.params?.id || vmParams?.id;
        const params = { ...vmParams, billno, id, remark };
        const data = { ...vmData, id, billno, remark };
        ttx.bill.approve(null, params, { data, billno, remark }, function (res) {
            const nextData = res?.data || res;
            if (nextData && typeof nextData === 'object') {
                vm._set_data?.('value', nextData, true);
                const currentMode = vm._get_data?.('currentMode') || 'browse';
                vm.execute?.('modeChange', currentMode);
                vm.execute?.('dataRefresh', { source: 'workflow-approve' });
            }
            reloadBillDetail(vm, 'workflow-approve-detail');
            // 通知审批侧边栏重新拉取流程（列表行/卡片头部入口都不会主动刷新面板）
            window.dispatchEvent(new CustomEvent('ttx:workflow:refresh', { detail: { billno, id } }));
            vm.execute('afterApprove', res);
        });
    },

    async reject(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        let remark = '';
        if (ctx?.skipPanel) {
            // 从审批侧边栏「驳回」直接进入，复用面板里已填的意见
            remark = ctx?.remark || '';
        } else {
            const result = await ttx.workflow?.open?.('reject', vm, {
                actionLabel: ctx?.button?.name || '驳回',
                // 列表操作列场景：当前行单据由 ctx.row 指定
                billId: ctx?.row?.id || '',
                // 面板内连续操作时复用已有节点
                steps: ctx?.approvalSteps,
            });
            if (!result?.confirmed) return;
            remark = result?.remark || '';
        }
        if (vm.execute('beforeReject', { ...ctx, remark }) === false) return;
        const vmParams = vm.getParams?.() || {};
        const vmData = vm.getData?.() || {};
        const billno = getBillno(vm);
        const id = vmData.id || vmParams?.params?.id || vmParams?.id;
        const params = { ...vmParams, billno, id, remark };
        const data = { ...vmData, id, billno, remark };
        ttx.bill.reject(null, params, { data, billno, remark }, function (res) {
            const nextData = res?.data || res;
            if (nextData && typeof nextData === 'object') {
                vm._set_data?.('value', nextData, true);
                const currentMode = vm._get_data?.('currentMode') || 'browse';
                vm.execute?.('modeChange', currentMode);
                vm.execute?.('dataRefresh', { source: 'workflow-reject' });
            }
            reloadBillDetail(vm, 'workflow-reject-detail');
            // 通知审批侧边栏重新拉取流程
            window.dispatchEvent(new CustomEvent('ttx:workflow:refresh', { detail: { billno, id } }));
            vm.execute('afterReject', res);
        });
    },

    async withdraw(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        // 撤回只是把已提交的单据撤回来，不需要打开审批流面板（撤回也没有必填意见）。
        // 列表行操作通过 ctx.row 传单据 id，卡片页走 vm 自身 params.id。
        const rowId = ctx?.row?.id || '';
        if (vm.execute('beforeWithdraw', { ...ctx, remark: '' }) === false) return;
        const vmParams = vm.getParams?.() || {};
        const vmData = vm.getData?.() || {};
        const billno = getBillno(vm);
        const id = rowId || vmData.id || vmParams?.params?.id || vmParams?.id;
        const params = { ...vmParams, billno, id };
        const data = rowId ? { id: rowId, billno } : { ...vmData, id, billno };
        ttx.bill.withdraw(null, params, { data, billno }, function (res) {
            if (rowId) {
                // 列表行操作：不能把单据数据写回列表 VM，直接按当前查询条件刷新列表
                ttx.toast?.success?.('撤回成功');
                _refreshVmData(vm);
            } else {
                const nextData = res?.data || res;
                if (nextData && typeof nextData === 'object') {
                    vm._set_data?.('value', nextData, true);
                    const currentMode = vm._get_data?.('currentMode') || 'browse';
                    vm.execute?.('modeChange', currentMode);
                    vm.execute?.('dataRefresh', { source: 'workflow-withdraw' });
                }
                reloadBillDetail(vm, 'workflow-withdraw-detail');
            }
            // 通知审批侧边栏重新拉取流程（撤回后状态回退，面板必须刷新）
            window.dispatchEvent(new CustomEvent('ttx:workflow:refresh', { detail: { billno, id } }));
            vm.execute('afterWithdraw', res);
        });
    },

    refresh(ctx) {
        var vm = ctx?.vm;
        if (!vm) return;
        // 列表页/页签卡片由 Meta 注册的 refresh 监听完成取数（列表会保留查询条件与分页）
        if (vm.hasEvent?.('refresh')) {
            vm.execute('refresh');
            return;
        }
        // 模态/抽屉卡片没有 Meta 监听 refresh 事件，由命令自己按 id 重拉详情
        _refreshVmData(vm);
    },

    /**
     * 审批流：只读查看审批流程（卡片头部、列表操作列的「审批流」按钮）
     * 列表场景 vm 是列表 VM，当前行由 ctx.row 传入，面板按行 id 拉取流程。
     */
    workflow(ctx = {}) {
        const { vm, row } = ctx;
        if (!vm) return;
        var rowId = row?.id || '';
        if (!rowId && vm.getCache?.('layoutType') === 'list') {
            ttx.toast?.warning?.('请选择一行记录查看审批流');
            return;
        }
        ttx.workflow?.view?.(vm, {
            billId: rowId || undefined,
            billno: getBillno(vm),
            title: row ? (row.name || row.code || row.billno || '') : '',
        });
    },

    print() {
        ttx.bill.print();
    },

    export(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        var billName = vm.getCache?.('billName') || vm.getCache?.('title') || '';
        var taskId = 'export_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
        window.dispatchEvent(new CustomEvent('ttx:download:add', {
            detail: { id: taskId, name: '导出文件', billName: billName, status: 'downloading', progress: 0 }
        }));
        // 导出完成后由 saveFile 或具体业务逻辑通过 ttx:download:update 更新状态
        vm._exportTaskId = taskId;
        vm.execute('beforeExport');
        vm.execute('export');
    },

    /**
     * 拉单：打开源单据列表页，供用户选择要拉取的数据。
     * 命令参数（commandArgs）：{ billno: "源单据号", metaid: "源单据列表metaId" }
     * 当前 VM 会作为 parentViewModel 传递给拉单列表，供转单时使用。
     */
    pull(ctx) {
        const { vm, button } = ctx;
        if (!vm) return;
        var args = {};
        try { if (button?.commandArgs) args = JSON.parse(button.commandArgs); } catch (e) {}
        const billno = args.billno || getBillno(vm);
        const metaid = args.metaid || '';
        if (!billno || !metaid) {
            ttx.toast?.warning?.('拉单配置缺少 billno 或 metaid');
            return;
        }
        // 拉单页默认新开系统级标签：将 openParams 提升为变量，
        // byBillNo 会设置 openParams.newOpenTabs = true（当 fromSystemTab 检测到时），
        // callback 中透传给 addItem，Pane 拦截器据此走 openServiceWithVM 开新标签
        var openParams = {
            billtype: 'card',
            billno,
            params: { mode: 'pull', query: { metaid } }
        };
        ttx.loader.runCommandLine('bill', openParams, vm, function (newVm, viewmeta, title) {
            var _mo2 = _getModalOpts(newVm, viewmeta);
            ttx.portalactions.addItem(billno, {
                title: title || '拉单列表',
                content: { vm: newVm, metaData: viewmeta, delay: _mo2.delay, extraConfig: _mo2.extraConfig },
                parent: vm,
                params: openParams,
            });
        });
    },

    /**
     * 转单：从拉单列表选中行 → 打开目标单据卡片页（新增模式）并预填数据。
     *
     * 链路：
     *   1. 拉单列表工具栏 TRANSFER 按钮 → HeadButton.onClick → ttx.command.execute('transfer', ctx)
     *   2. 本函数通过 containerKey 定位表格 API → getSelectRows() 取选中行
     *   3. runCommandLine('bill', {mode:'add'}) → voucher.init → 创建目标卡片 VM
     *   4. 回调里将选中行数据写入 VM：
     *      a. 单条：静默 _set_data('value', rowData, true) → Form 挂载时 setListenerState 读取
     *      b. 多条：额外 setCache('_transferRows/Index/Metas')
     *         → Meta 渲染 TransferRowListMeta（左侧列表）
     *         → 点击列表项 → 静默 _set_data + modeChange('edit') → Form 刷新
     *
     * 命令参数（commandArgs）：{ billno: "目标单据号", metaid: "目标单据卡片metaId" }
     */
    transfer(ctx) {
        const { vm, button, containerKey } = ctx;
        if (!vm) return;

        // 解析命令参数
        var args = {};
        try { if (button?.commandArgs) args = JSON.parse(button.commandArgs); } catch (e) {}
        const targetBillno = args.billno || getBillno(vm);
        const metaid = args.metaid || '';
        if (!targetBillno || !metaid) {
            ttx.toast?.warning?.('转单配置缺少 billno 或 metaid');
            return;
        }

        // 从拉单列表的表格 API 获取选中行（通过 containerKey 定位，多表格场景按命名空间隔离）
        var selectedRows = [];
        var tableApi = null;
        if (containerKey) {
            tableApi = vm.getCache?.('tableApi_' + containerKey);
        }
        if (!tableApi) {
            tableApi = vm.getCache?.('tableApi');
        }
        if (tableApi) {
            if (typeof tableApi.getSelectRows === 'function') {
                selectedRows = tableApi.getSelectRows() || [];
            }
        }
        if (!selectedRows.length) {
            ttx.toast?.warning?.('请先选择要转单的行');
            return;
        }

        // getSelectRows 返回索引数组如 [0,1,2]，需用 getData 取出实际行数据对象
        if (typeof selectedRows[0] === 'number' && typeof tableApi.getData === 'function') {
            var allData = tableApi.getData() || [];
            var dataRows = [];
            for (var i = 0; i < selectedRows.length; i++) {
                var row = allData[selectedRows[i]];
                if (row) dataRows.push(row);
            }
            if (dataRows.length) {
                selectedRows = dataRows;
            }
        }

        // 以下变量必须在索引转数据之后取值，否则 billData 是数字
        var billData = selectedRows[0];          // 首行预填表单
        var isMulti = selectedRows.length > 1;   // 多条 → 渲染左侧转移列表
        // 父级 VM：优先取 parentViewModel（拉单时由 byBillNo 写入），兜底用当前 vm
        var parentVm = vm.getCache?.('parentViewModel') || vm;

        // 转单页默认新开系统级标签：将 openParams 提升为变量，
        // byBillNo 会设置 openParams.newOpenTabs = true（mode='add' 时），
        // callback 中透传给 addItem，Pane 拦截器据此走 openServiceWithVM 开新标签
        var openParams = {
            billtype: 'card',
            billno: targetBillno,
            params: {
                mode: 'add',
                query: { metaid },
            }
        };
        ttx.loader.runCommandLine('bill', openParams, parentVm, function (newVm, viewmeta, title) {
            // ---- 预填表单数据（过滤系统字段） ----
            if (billData && typeof billData === 'object') {
                // 转单时不应携带源单据的系统字段
                var skipFields = { id:1, ID:1, Id:1, _id:1, status:1, createTime:1, createdTime:1, modifyTime:1, modifiedTime:1, createdAt:1, updatedAt:1, createdBy:1, modifiedBy:1, modifier:1, creator:1, version:1, isDeleted:1, enable:1, _key:1, _rowIndex:1 };
                var cleanData = {};
                for (var k in billData) {
                    if (billData.hasOwnProperty(k) && !skipFields[k]) {
                        cleanData[k] = billData[k];
                    }
                }
                newVm._set_data?.('value', cleanData);
            }
            if (draftAutoSaveEnabled(newVm)) ttx.draft?.checkOnAdd?.(newVm);
            if (draftAutoSaveEnabled(newVm)) ttx.draft?.enableAutoSave?.(newVm);
            var _mo3 = _getModalOpts(newVm, viewmeta);
            ttx.portalactions.addItem(targetBillno, {
                title: title || '转单',
                content: { vm: newVm, metaData: viewmeta, delay: _mo3.delay, extraConfig: _mo3.extraConfig },
                parent: parentVm,
                // 透传打开参数（含 newOpenTabs），供下游 Pane 拦截器读取，决定新开系统级标签
                // ============================================================
                // 【动态控制新开标签】调整点：如果后续需要按条件控制转单是否新开标签，
                // 在这里修改 openParams.newOpenTabs 的值即可（byBillNo 默认 mode='add' 时为 true）
                // ============================================================
                params: openParams,
            });

            // addItem 已同步 dispatch，但 Form 要等 React commit 后才挂载。
            // 需要同时设 currentMode + pageMode + modeChange，不然按钮不更新。
            setTimeout(function () {
                newVm._set_data?.('currentMode', 'edit', true);
                newVm._set_data?.('pageMode', 'edit', true);
                newVm.execute?.('modeChange', 'edit');
            }, 50);

            // ---- 多条转单：缓存选中行+列配置到 VM，Pane 层渲染 TransferRowList ----
            if (isMulti) {
                newVm.setCache?.('_transferRows', selectedRows);
                newVm.setCache?.('_transferIndex', 0);
                newVm.setCache?.('_transferMetas', viewmeta);
                // 源表格列配置，供左侧列表按列展示
                if (typeof tableApi.getColumns === 'function') {
                    newVm.setCache?.('_transferColumns', tableApi.getColumns());
                }
            }
        });
    },

    /** 上一张单据：从父级列表 dataSource 中找到当前记录的上一条，加载详情 */
    prevBill(ctx) {
        switchBill(ctx, -1);
    },

    /** 下一张单据：从父级列表 dataSource 中找到当前记录的下一条，加载详情 */
    nextBill(ctx) {
        switchBill(ctx, 1);
    },

    /** 侧边栏：优先通知当前 VM 对应页签，旧的 document 广播仅作为兼容兜底 */
    sidebar(ctx) {
        var toggleSidebar = ctx.vm?.getCache?.('toggleSidebar');
        if (typeof toggleSidebar === 'function') {
            toggleSidebar();
            return;
        }
        var paneIndex = ctx.vm?.getCache?.('paneIndex');
        document.dispatchEvent(new CustomEvent('ttx:sidebar:toggle', { detail: { paneIndex: paneIndex } }));
    },

    /** 附件助手侧边栏：和 sidebar 一样的命令驱动方式 */
    attachmentSidebar(ctx) {
        var toggleAttachmentSidebar = ctx.vm?.getCache?.('toggleAttachmentSidebar');
        if (typeof toggleAttachmentSidebar === 'function') {
            toggleAttachmentSidebar();
            return;
        }
        var paneIndex = ctx.vm?.getCache?.('paneIndex');
        document.dispatchEvent(new CustomEvent('ttx:attachment-sidebar:toggle', { detail: { paneIndex: paneIndex } }));
    },

    /** 员工信息侧边栏：VM cache 优先，document 事件兜底 */
    employeeSidebar(ctx) {
        var toggle = ctx.vm?.getCache?.('toggleEmployeeSidebar');
        if (typeof toggle === 'function') {
            toggle();
            return;
        }
        var paneIndex = ctx.vm?.getCache?.('paneIndex');
        document.dispatchEvent(new CustomEvent('ttx:employee-sidebar:toggle', { detail: { paneIndex: paneIndex } }));
    },

    /** 供应商信息侧边栏：VM cache 优先，document 事件兜底 */
    supplierSidebar(ctx) {
        var toggle = ctx.vm?.getCache?.('toggleSupplierSidebar');
        if (typeof toggle === 'function') {
            toggle();
            return;
        }
        var paneIndex = ctx.vm?.getCache?.('paneIndex');
        document.dispatchEvent(new CustomEvent('ttx:supplier-sidebar:toggle', { detail: { paneIndex: paneIndex } }));
    },

    /** 历史记录命令：具体业务逻辑委托给 tx.history.js 统一处理。 */
    history(ctx) {
        return ttx.history?.open?.(ctx);
    },
    // ========== 草稿功能（委托 tx.draft.js） ==========

    draftCheckOnAdd(ctx)  { ttx.draft?.checkOnAdd(ctx.vm); },
    enableDraftAutoSave(ctx)  { ttx.draft?.enableAutoSave(ctx.vm); },
    disableDraftAutoSave(ctx) { ttx.draft?.disableAutoSave(ctx.vm); },
    draftClear(ctx) { ttx.draft?.clear(ctx.vm); },
    /** 手动保存草稿：弹窗输入草稿名 → 保存 */
    async DRAFT_SAVE(ctx) {
        var name = await ttx.modal.prompt({
            title: '保存草稿',
            message: '请输入草稿名称，方便后续查找恢复：',
            placeholder: '例如：报销单-张三',
            confirmText: '保存',
            cancelText: '取消'
        });
        if (name === null) return;
        ttx.draft?.save(ctx.vm, name);
        ttx.toast.success('草稿已保存');
    },

    /** 返回上一页：通过事件通知 Meta 层关闭页签，而非直接操作 Redux */
    back(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        // 新增页主动返回：清除本次自动保存的草稿，避免下次新增误弹「草稿恢复」
        if (vm._get_data?.('pageMode') === 'add') ttx.draft?.clearAuto(vm);
        // 系统级多标签：直接关闭系统级页签，不走 requestClose（页签栈内回退）
        if (closeSystemTabIfNeed(vm)) return;
        vm.execute('beforeBack');
        // 通过 requestClose 事件通知 transfer/Meta 层执行 portalactions.delItem
        vm.execute('requestClose', { refreshData: true, source: 'card-back' });
    },

    /** 同 back，兼容历史调用 */
    return(ctx) {
        const { vm } = ctx;
        if (!vm) return;
        // 新增页主动返回：清除本次自动保存的草稿，避免下次新增误弹「草稿恢复」
        if (vm._get_data?.('pageMode') === 'add') ttx.draft?.clearAuto(vm);
        // 系统级多标签：同 back，直接关闭系统级页签
        if (closeSystemTabIfNeed(vm)) return;
        vm.execute('beforeBack');
        vm.execute('requestClose', { refreshData: true, source: 'card-return' });
    },

});

// ============================================================
// 列表命令（list scope）
// ============================================================
ttx.command.registerScope('list', {

    querySolution(ctx = {}) {
        const { vm, params } = ctx;
        if (!vm) return;
        const openQuerySolutionModal = vm.getCache?.('openQuerySolutionModal');
        if (typeof openQuerySolutionModal !== 'function') return;
        return openQuerySolutionModal(params || {});
    },

    /** 导入：打开导入弹框上传文件并执行导入 */
    import(ctx = {}) {
        ttx.ioTemplate?.import?.(ctx.vm);
    },

    /** 下载导入模板：选择模板后下载 Excel */
    import_template(ctx = {}) {
        ttx.ioTemplate?.importTemplate?.(ctx.vm);
    },

    /** 导出：选择导出模板后导出 Excel */
    export(ctx = {}) {
        ttx.ioTemplate?.export?.(ctx.vm);
    },

});

// ============================================================
// 表格命令（table scope）
// ============================================================
ttx.command.registerScope('table', {

    /**
     * 新增行
     * 优先按 containerKey 定位目标表格（支持同 VM 下多表格场景），
     * 找不到时回退到全局 tableApi（兼容旧逻辑）。
     * 仅读全局 tableApi 会导致多表格场景下指向第一个挂载的表格，
     * 用户在第二个表格点击新增行时无反应。
     */
    addrow(ctx) {
        const { vm, containerKey } = ctx;
        var vmId = vm ? (vm._get_data?.('serviceCode') || vm.getParams?.()?.billno || 'unknown') : 'null';
        console.log('[addRow][CMD] addrow 开始, vm=' + vmId + ' containerKey=' + containerKey);
        let tableApi = null;
        let source = '';
        if (containerKey) {
            tableApi = vm?.getCache?.('tableApi_' + containerKey);
            source = 'scoped(tableApi_' + containerKey + ')';
        }
        if (!tableApi) {
            tableApi = vm?.getCache?.('tableApi');
            source = 'global(tableApi)';
        }
        console.log('[addRow][CMD] tableApi=' + (tableApi ? 'found via ' + source : 'NOT FOUND') + ' vm=' + vmId);
        // 打印 vm 中所有 tableApi 相关键
        var cache = vm ? vm._get_data?.('cache') : null;
        var tableApiKeys = cache ? Object.keys(cache).filter(function(k) { return k.indexOf('tableApi') === 0; }) : [];
        console.log('[addRow][CMD] vm中tableApi键: ' + JSON.stringify(tableApiKeys) + ' vm=' + vmId);
        if (tableApi && typeof tableApi.addRow === 'function') {
            tableApi.addRow();
        } else {
            console.warn('[addRow][CMD] 失败：tableApi为空或无addRow方法, containerKey=' + containerKey + ' vm=' + vmId);
        }
    },

    /**
     * 删除行
     * 同 addrow，优先按 containerKey 定位目标表格，回退全局 tableApi。
     * rowIndex 指定时删除指定行，否则删除当前选中行。
     */
    deleterow(ctx) {
        const { vm, rowIndex, containerKey } = ctx;
        if (!vm) return;
        let tableApi = null;
        if (containerKey) {
            tableApi = vm.getCache?.('tableApi_' + containerKey);
        }
        if (!tableApi) {
            tableApi = vm.getCache?.('tableApi');
        }
        if (rowIndex !== undefined && rowIndex !== null) {
            tableApi?.deleteRecord?.(rowIndex);
        } else {
            tableApi?.deleteRow?.();
        }
    },

    editrow(ctx) {
        const { vm, rowIndex, rowData } = ctx;
        if (!vm) return;
        switchMode(vm, 'edit');
        if (rowData) {
            vm._set_data?.('value', rowData);
        }
        vm.execute?.('onEditRow', { rowIndex, rowData });
    },

});

// ============================================================
// 更新公告（announcement scope）
// ============================================================
ttx.command.register('showAnnouncement', function (ctx) {
    if (!ctx) return;
    ttx.announcement.show({
        id: ctx.announcementId || ctx.id,
        title: ctx.title,
        content: ctx.content,
        version: ctx.version,
        footer: ctx.footer,
        forceShow: ctx.forceShow,
        width: ctx.width,
        confirmText: ctx.confirmText,
        onConfirm: ctx.onConfirm,
        onClose: ctx.onClose,
    });
});

// ============================================================
// 联动查询（jointQuery scope）
// ============================================================
const _jointQueryOpenLocks = Object.create(null);

ttx.command.register('jointQuery', function (ctx) {
    const { vm, jointQueryParams } = ctx;
    if (!vm) return;
    const billno = getBillno(vm);
    const metaid = vm._get_data?.('template')?.view?.cCardKey || '';

    const record = jointQueryParams?.originData || jointQueryParams?.record || {};

    // 处理 record.id 可能是对象 {id: "xxx:yyy"} 的情况
    var rawId = record.id || record.ID || record._id || record.key || '';
    var recordId = (typeof rawId === 'object' && rawId !== null) ? (rawId.id || rawId.ID || rawId._id || '') : rawId;
    const billCode = record.code || record.billcode || record.billCode || record.codeNo || '';

    const openKey = [billno, metaid, recordId].join('::');

    // Same bill click bursts should only trigger one open request.
    if (_jointQueryOpenLocks[openKey]) return;
    _jointQueryOpenLocks[openKey] = true;

    const releaseLock = function () {
        setTimeout(function () {
            delete _jointQueryOpenLocks[openKey];
        }, 500);
    };

    // 构造 openParams，添加 newOpenTabs: true 以支持新开系统级标签
    // metaid 保持干净，不拼 recordId；recordId 放在 query.id 中
    // billCode 持久化，供刷新时重建标题
    var openParams = {
        billtype: 'card',
        billno,
        params: { mode: 'browse', query: { metaid: metaid, id: recordId }, billCode: billCode },
        newOpenTabs: true
    };

    ttx.loader.runCommandLine('bill', openParams, vm, function (newVm, viewmeta, title) {
        var _mo4 = _getModalOpts(newVm, viewmeta);
        // 不再在此处拼接 billCode，统一由 Core 工程的 openServiceWithVM/refreshService 处理
        // billCode 已持久化到 openParams.params.billCode，供 Core 工程读取拼接
        var finalTitle = title || viewmeta?.title || '';
        // 透传 openParams 给 addItem，供 Pane 拦截器识别为新开系统级标签
        ttx.portalactions.addItem(billno, {
            title: finalTitle,
            content: { vm: newVm, metaData: viewmeta, delay: _mo4.delay, extraConfig: _mo4.extraConfig },
            parent: vm,
            params: openParams,
        });
        // addItem 已同步 dispatch，但 Form 要等 React commit 后才挂载。
        // 延迟触发 modeChange，确保组件已注册监听。
        setTimeout(function () {
            newVm._set_data?.('currentMode', 'browse', true);
            newVm._set_data?.('pageMode', 'browse', true);
            newVm.execute?.('modeChange', 'browse');
        }, 50);
        releaseLock();
    });
});

// ============================================================
// 单据联查（打开子应用"单据联查"页面，新开系统级页签）
// 不依赖 menu.js 配置，命令内部直接构造 subapp 参数调用 ttx.portal.open
// 子应用路由：/system/biz-trace（业务流上下游关系画布）
// ============================================================
ttx.command.register('billTrace', function (ctx) {
    // 子应用 entry 域名：优先取 SDK 全局配置 ttx.config.subAppEntry，开发环境默认 localhost:8082
    var subAppBase = (ttx.config && ttx.config.subAppEntry)
        || (typeof process !== 'undefined' && process.env && process.env.SUB_APP_ENTRY)
        || 'http://localhost:8082';
    // 单据联查页面固定路由
    var traceRoute = '/system/biz-trace';

    // TODO: 后续从 ctx / vm 提取单据信息（billno、id 等）拼到 URL query，
    // 当前先写死固定数据，后面再决定传哪些参数
    var fixedParams = 'billno=service01&id=0';
    var url = subAppBase + traceRoute + '?' + fixedParams;

    var params = {
        billtype: 'subapp',
        billno: 'bill-trace',
        params: {
            type: 'iframe',
            url: url,
            title: ctx?.title || '单据联查',
        }
    };
    ttx.portal.open(params);
});



